import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePromotion, creditPurchaseBonus } from '../lib/promotions';
import { useTheme } from '../context/ThemeContext';
import { useRazorpay } from 'react-razorpay';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TIME_SLOTS, getEndSlots } from '../utils/timeSlots';
import { toast } from 'sonner';
import { ServicesTab } from '../components/client/ServicesTab';
import { OrganizationTab } from '../components/client/OrganizationTab';
import ClientVirtualOfficeView from '../components/virtual_office/ClientVirtualOfficeView';
import { ClientEcosystemPanel } from '../components/ecosystem/ClientEcosystemPanel';
import { 
  Calendar, 
  Clock, 
  CreditCard, 
  FileText, 
  Loader2, 
  Plus,
  Wallet,
  X,
  ArrowUpRight,
  Building2,
  Users,
  TrendingUp,
  Coins,
  RefreshCw,
  AlertTriangle,
  CalendarClock,
  Download,
  LogOut,
  User,
  Camera,
  Send,
  ImagePlus,
  Ticket
} from 'lucide-react';
import { ProfileModal } from '../components/ProfileModal';
import { QuickBookDayPassModal } from '../components/day_passes/QuickBookDayPassModal';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, api, logout } = useAuth();
  const promotion = usePromotion();
  const { isDark } = useTheme();
  const { Razorpay } = useRazorpay();

  // Map URL path to tab
  const getTabFromPath = () => {
    const path = location.pathname;
    if (path === '/bookings') return 'history';
    if (path === '/invoices') return 'invoices';
    if (path === '/credits') return 'credits';
    return 'upcoming';
  };
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [dayPassBalance, setDayPassBalance] = useState(null);
  const [showQuickBookDayPass, setShowQuickBookDayPass] = useState(false);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  // Deep link from the website (e.g. the Diwali members page): /dashboard?buy=credits
  useEffect(() => {
    if (new URLSearchParams(location.search).get('buy') === 'credits') setShowBuyCreditsModal(true);
  }, [location.search]);
  const [creditTiers, setCreditTiers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [buyingCredits, setBuyingCredits] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Ticket states
  const [tickets, setTickets] = useState([]);
  const [ticketDesc, setTicketDesc] = useState('');
  const [ticketVenueId, setTicketVenueId] = useState('');
  const [ticketPhoto, setTicketPhoto] = useState(null);
  const [ticketUploading, setTicketUploading] = useState(false);
  const [submittingTicket, setSubmittingTicket] = useState(false);
  
  // Cancel & Reschedule states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [refundEstimate, setRefundEstimate] = useState(null);
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({
    new_date: '',
    new_start_time: '',
    new_end_time: ''
  });

  // Pay Now (pending-payment bookings) state
  const [payingBookingId, setPayingBookingId] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    fetchData();
  }, [api, user]);

  // Only tick while there's at least one pending-payment booking to count down —
  // avoids a needless 1s re-render loop the rest of the time.
  const hasPendingPayment = bookings.some(b => b.status === 'pending');
  useEffect(() => {
    if (!hasPendingPayment) return;
    const interval = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [hasPendingPayment]);

  // Bookings created via self-service checkout must be paid within 12 minutes
  // (backend/payment_timeout_scheduler.py auto-cancels + releases the slot
  // after this window — this mirrors that deadline client-side).
  const PAYMENT_WINDOW_MS = 12 * 60 * 1000;
  const getPaymentWindow = (booking) => {
    if (!booking.created_at) return { expired: true, msLeft: 0 };
    const deadline = new Date(booking.created_at).getTime() + PAYMENT_WINDOW_MS;
    const msLeft = deadline - nowTick;
    return { expired: msLeft <= 0, msLeft: Math.max(0, msLeft) };
  };
  const formatCountdown = (ms) => {
    const totalSec = Math.ceil(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handlePayNow = async (booking) => {
    setPayingBookingId(booking.id);
    try {
      const orderRes = await api.post('/payments/create-order', {
        booking_id: booking.id,
        amount: booking.total_amount,
        currency: 'INR'
      });
      const { order_id, amount, currency, key_id } = orderRes.data;
      // Cap the checkout's own countdown to whatever's left of this booking's
      // payment window, so Razorpay auto-closes the modal at the same moment
      // the backend sweep would cancel the booking (see getPaymentWindow above).
      const secondsLeft = Math.max(1, Math.floor(getPaymentWindow(booking).msLeft / 1000));
      const options = {
        key: key_id,
        amount,
        currency,
        name: 'Clayworks',
        description: `Booking ${booking.id?.slice(0, 8)} - ${booking.booking_date}`,
        order_id,
        timeout: secondsLeft,
        handler: async (response) => {
          try {
            await api.post('/payments/verify', {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              booking_id: booking.id
            });
            toast.success('Payment successful! Booking confirmed.');
            setBookings(prev => prev.map(b =>
              b.id === booking.id ? { ...b, status: 'confirmed', payment_status: 'completed' } : b
            ));
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Payment verification failed');
            fetchData(); // resync — booking may have just been auto-cancelled by the timeout sweep
          } finally {
            setPayingBookingId(null);
          }
        },
        prefill: { name: user?.name, email: user?.email, contact: user?.phone },
        theme: { color: '#c8a97e' },
        modal: { ondismiss: () => setPayingBookingId(null) }
      };
      const rzpInstance = new Razorpay(options);
      rzpInstance.on('payment.failed', (response) => {
        toast.error(`Payment failed: ${response.error?.description || 'Please try again'}`);
        setPayingBookingId(null);
      });
      rzpInstance.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setPayingBookingId(null);
      fetchData();
    }
  };

  const fetchData = async () => {
    try {
      const [bookingsRes, invoicesRes, walletRes, transRes, tiersRes, venuesRes] = await Promise.all([
        api.get('/bookings'),
        api.get('/invoices'),
        api.get('/credits/wallet'),
        api.get('/credits/transactions'),
        api.get('/credits/tiers'),
        api.get('/venues')
      ]);
      setBookings(bookingsRes.data);
      setInvoices(invoicesRes.data);
      setWallet(walletRes.data);
      setTransactions(transRes.data);
      setCreditTiers(tiersRes.data.tiers || []);
      setVenues(venuesRes.data || []);

      // Fetch tickets
      try {
        const ticketsRes = await api.get('/tickets');
        setTickets(ticketsRes.data);
      } catch (e) { /* tickets not critical */ }

      // Fetch Day Pass balance
      try {
        const dayPassRes = await api.get('/day-pass-packages/my-balance');
        setDayPassBalance(dayPassRes.data);
      } catch (e) { /* day pass balance not critical */ }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTier = (tier) => {
    setSelectedTier(tier);
    setShowCheckout(true);
  };

  const handleBuyCredits = async () => {
    if (!selectedTier) {
      toast.error('Please select a credit tier');
      return;
    }
    
    setBuyingCredits(true);
    try {
      // Create Razorpay order for credit purchase
      const orderRes = await api.post('/payments/credit-order', { credits: selectedTier.credits });

      const options = {
        key: orderRes.data.key_id,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        name: 'Clayworks',
        description: `${selectedTier.credits} Credits (${selectedTier.label} Tier)`,
        order_id: orderRes.data.order_id,
        handler: async (response) => {
          try {
            const verifyRes = await api.post('/payments/verify-credit', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            const bonus = verifyRes.data?.bonus_credits || 0;
            toast.success(bonus
              ? `${selectedTier.credits} credits + ${bonus} bonus credits added — ${selectedTier.credits + bonus} in total!`
              : `Successfully purchased ${selectedTier.credits} credits!`);
            setShowBuyCreditsModal(false);
            setShowCheckout(false);
            setSelectedTier(null);
            fetchData();
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
          setBuyingCredits(false);
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: user?.phone || ''
        },
        theme: { color: '#c8a97e' },
        modal: {
          ondismiss: () => {
            toast.error('Payment cancelled');
            setBuyingCredits(false);
          }
        }
      };

      const rzpInstance = new Razorpay(options);
      rzpInstance.on('payment.failed', (response) => {
        toast.error(`Payment failed: ${response.error.description}`);
        setBuyingCredits(false);
      });
      rzpInstance.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setBuyingCredits(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    setCancelling(true);
    try {
      const res = await api.post(`/bookings/${bookingId}/cancel`);
      toast.success(`Booking cancelled. ${res.data.credits_refunded > 0 ? `${res.data.credits_refunded.toFixed(0)} credits refunded (${res.data.refund_percentage}%)` : 'No refund due to policy.'}`);
      setBookings(prev => prev.map(b => 
        b.id === bookingId ? { ...b, status: 'cancelled' } : b
      ));
      setShowCancelModal(false);
      setSelectedBooking(null);
      setRefundEstimate(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const openCancelModal = async (booking) => {
    setSelectedBooking(booking);
    setShowCancelModal(true);
    setLoadingRefund(true);
    try {
      const res = await api.get(`/bookings/${booking.id}/refund-estimate`);
      setRefundEstimate(res.data);
    } catch (error) {
      console.error('Failed to get refund estimate:', error);
    } finally {
      setLoadingRefund(false);
    }
  };

  const openRescheduleModal = (booking) => {
    setSelectedBooking(booking);
    setRescheduleForm({
      new_date: booking.booking_date,
      new_start_time: booking.start_time,
      new_end_time: booking.end_time
    });
    setShowRescheduleModal(true);
  };

  const handleReschedule = async () => {
    if (!rescheduleForm.new_date || !rescheduleForm.new_start_time || !rescheduleForm.new_end_time) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setRescheduling(true);
    try {
      const res = await api.post(`/bookings/${selectedBooking.id}/reschedule`, rescheduleForm);
      const feeMsg = res.data.fee_percentage > 0 
        ? `${res.data.fee_percentage}% fee applied (₹${res.data.rescheduling_fee?.toFixed(0) || 0})` 
        : 'No fee charged!';
      toast.success(`Booking rescheduled successfully! ${feeMsg}`);
      fetchData();
      setShowRescheduleModal(false);
      setSelectedBooking(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule booking');
    } finally {
      setRescheduling(false);
    }
  };

  const upcomingBookings = bookings.filter(b => 
    ['pending', 'confirmed'].includes(b.status) && 
    new Date(b.booking_date) >= new Date()
  );

  const pastBookings = bookings.filter(b => 
    b.status === 'completed' || 
    b.status === 'cancelled' ||
    new Date(b.booking_date) < new Date()
  );

  const handleDownloadInvoice = async (bookingId) => {
    try {
      toast.info('Generating invoice...');
      await api.get(`/bookings/${bookingId}/invoice`);
      const res = await api.get(`/bookings/${bookingId}/invoice/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Clayworks_Invoice_${bookingId.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download invoice');
    }
  };


  const totalSpent = bookings
    .filter(b => b.payment_status === 'completed')
    .reduce((sum, b) => sum + (b.total_amount || 0), 0);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30';
      case 'pending': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-500 border-red-500/30';
      case 'completed': return 'bg-primary/20 text-primary border-primary/30';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const venueName = (venueId) => venues.find(v => v.id === venueId)?.name || 'Unknown venue';

  // Theme-aware colors
  const cardBg = isDark ? 'bg-[#13161a]' : 'bg-white';
  const cardBorder = isDark ? 'border-[#1e2128]' : 'border-[#e5dfd0]';
  const innerCardBg = isDark ? 'bg-[#0a0b0d]' : 'bg-[#faf8f3]';

  const handleTicketPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTicketUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTicketPhoto(res.data.url || res.data.file_url);
      toast.success('Photo attached');
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setTicketUploading(false);
    }
  };

  const handleSubmitTicket = async () => {
    if (!ticketDesc.trim()) {
      toast.error('Please describe the issue');
      return;
    }
    setSubmittingTicket(true);
    try {
      await api.post('/tickets', {
        description: ticketDesc.trim(),
        photo_url: ticketPhoto,
        venue_id: ticketVenueId || null,
      });
      toast.success('Ticket submitted successfully');
      setTicketDesc('');
      setTicketPhoto(null);
      setTicketVenueId('');
      // Refresh tickets
      const res = await api.get('/tickets');
      setTickets(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit ticket');
    } finally {
      setSubmittingTicket(false);
    }
  };

  const getTicketStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-red-500/20 text-red-500 border-red-500/30';
      case 'in_progress': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
      case 'resolved': return 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30';
      case 'closed': return 'bg-muted text-muted-foreground';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8" data-testid="client-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">Welcome back, {user?.name?.split(' ')[0]}</h1>
            <p className="text-muted-foreground mt-1">Here's what's happening with your bookings</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => navigate('/booking')}
              className="bg-primary hover:bg-primary/90 shadow-lg shadow-primary/25"
              data-testid="new-booking-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New Booking
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowProfileModal(true)}
              className={cardBorder}
              data-testid="sidebar-profile-btn"
            >
              {user?.photo_url ? (
                <img src={user.photo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${user.photo_url}` : user.photo_url} alt="" className="w-5 h-5 rounded-full object-cover mr-1.5" />
              ) : (
                <User className="w-4 h-4 mr-1.5" />
              )}
              Profile
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-muted-foreground hover:text-destructive"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card className={`${cardBg} ${cardBorder} hover:border-primary/30 transition-colors`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Upcoming</p>
                  <p className="text-3xl font-heading font-bold mt-1">{upcomingBookings.length}</p>
                  <p className="text-xs text-muted-foreground mt-2">bookings scheduled</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`${cardBg} ${cardBorder} hover:border-primary/30 transition-colors`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Bookings</p>
                  <p className="text-3xl font-heading font-bold mt-1">{bookings.length}</p>
                  <p className="text-xs text-muted-foreground mt-2">all time</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={`${cardBg} ${cardBorder} hover:border-primary/30 transition-colors`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Spent</p>
                  <p className="text-3xl font-heading font-bold mt-1">₹{totalSpent.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground mt-2">on bookings</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Credit Balance Card - Show for all users */}
          <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/30 hover:border-primary/50 transition-colors cursor-pointer" onClick={() => setShowBuyCreditsModal(true)} data-testid="client-credit-balance-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-primary/80">Credit Balance</p>
                  <p className="text-3xl font-heading font-bold mt-1 text-primary" data-testid="client-credit-balance-total">
                    {((wallet?.balance || 0) + (wallet?.topup_balance || 0)).toFixed(0)}
                  </p>
                  {(wallet?.topup_balance || 0) > 0 ? (
                    <p className="text-xs text-primary/60 mt-2">
                      {(wallet?.balance || 0).toFixed(0)} monthly + {(wallet?.topup_balance || 0).toFixed(0)} top-up
                    </p>
                  ) : (
                    <p className="text-xs text-primary/60 mt-2">
                      {wallet?.is_organization_wallet ? 'org credits' : 'available credits'}
                    </p>
                  )}
                  {(wallet?.promo_balance || 0) > 0 && (
                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mt-1" data-testid="client-promo-credit-balance">
                      + {Number(wallet.promo_balance).toLocaleString('en-IN', { maximumFractionDigits: 1 })} bonus Meeting Room Credits
                      {wallet.promo_valid_until && ` · valid till ${new Date(wallet.promo_valid_until).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' })}`}
                    </p>
                  )}
                </div>
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Coins className="w-5 h-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Day Pass Balance Card - Show for all users */}
          <Card className="bg-gradient-to-br from-amber-500/20 to-amber-500/5 border-amber-500/30 hover:border-amber-500/50 transition-colors" data-testid="client-day-pass-balance-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-amber-700 dark:text-amber-400">Bulk Day Pass Balance</p>
                  <p className="text-3xl font-heading font-bold mt-1 text-amber-600 dark:text-amber-400" data-testid="client-day-pass-balance-total">
                    {dayPassBalance?.total_remaining || 0}
                  </p>
                  <p className="text-xs text-amber-700/70 dark:text-amber-400/70 mt-2">passes remaining</p>
                  {(() => {
                    const free = (dayPassBalance?.packages || []).filter(p => p.offer && p.status === 'active' && (p.quantity_remaining || 0) > 0);
                    const n = free.reduce((sum, p) => sum + p.quantity_remaining, 0);
                    if (!n) return null;
                    const until = free.map(p => p.expiry_date).sort()[0];
                    return (
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mt-1" data-testid="client-free-day-pass-expiry">
                        incl. {n} free · expire {new Date(until).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    );
                  })()}
                </div>
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
              {(dayPassBalance?.total_remaining || 0) > 0 && (
                <Button
                  size="sm"
                  className="w-full mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => setShowQuickBookDayPass(true)}
                  data-testid="quick-book-day-pass-open-btn"
                >
                  Book Now
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue={getTabFromPath()} className="space-y-6">
          <TabsList className={`${cardBg} border ${cardBorder} p-1`}>
            <TabsTrigger value="upcoming" data-testid="tab-upcoming" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Upcoming
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              History
            </TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Invoices
            </TabsTrigger>
            <TabsTrigger value="credits" data-testid="tab-credits" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Credits
            </TabsTrigger>
            <TabsTrigger value="day_passes" data-testid="tab-day-passes" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Bulk Day Passes
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Services
            </TabsTrigger>
            {user?.organization_id && (
              <TabsTrigger value="organization" data-testid="tab-organization" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                Organization
              </TabsTrigger>
            )}
            <TabsTrigger value="tickets" data-testid="tab-tickets" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Raise Ticket
            </TabsTrigger>
            <TabsTrigger value="virtual_office" data-testid="tab-virtual-office" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Virtual Office
            </TabsTrigger>
            <TabsTrigger value="ecosystem" data-testid="tab-ecosystem" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              Ecosystem
            </TabsTrigger>
          </TabsList>

          {/* Upcoming Bookings */}
          <TabsContent value="upcoming" className="space-y-4">
            {upcomingBookings.length === 0 ? (
              <Card className={`${cardBg} ${cardBorder}`}>
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-2">No upcoming bookings</h3>
                  <p className="text-muted-foreground mb-6">Book a workspace to get started</p>
                  <Button onClick={() => navigate('/booking')} className="bg-primary hover:bg-primary/90">
                    Book Now
                  </Button>
                </CardContent>
              </Card>
            ) : (
              upcomingBookings.map(booking => (
                <Card key={booking.id} className={`${cardBg} ${cardBorder} hover:border-primary/30 transition-all`} data-testid={`booking-${booking.id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <Badge className={`${getStatusColor(booking.status)} border`}>{booking.status}</Badge>
                          <span className="text-xs text-muted-foreground font-mono">#{booking.id?.slice(0, 8)}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <Calendar className="w-4 h-4 mr-2 text-primary" />
                          <span className="font-medium">{booking.booking_date}</span>
                          <span className="mx-2 text-muted-foreground">•</span>
                          <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
                          <span className="text-muted-foreground">{booking.start_time} - {booking.end_time}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center">
                            <Building2 className="w-4 h-4 mr-2" />
                            {venueName(booking.venue_id)}
                          </div>
                          <div className="flex items-center">
                            <Users className="w-4 h-4 mr-2" />
                            {booking.attendees || 1} {(booking.attendees || 1) === 1 ? 'person' : 'people'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right mr-2">
                          <p className="text-xl font-heading font-bold">₹{booking.total_amount?.toFixed(0)}</p>
                          <p className="text-xs text-muted-foreground">
                            {booking.payment_status === 'completed'
                              ? 'Paid'
                              : booking.status === 'pending' && !getPaymentWindow(booking).expired
                                ? `Payment Pending · ${formatCountdown(getPaymentWindow(booking).msLeft)} left`
                                : 'Payment Pending'}
                          </p>
                        </div>
                        {booking.status === 'pending' && booking.total_amount > 0 && !getPaymentWindow(booking).expired && (
                          <Button
                            size="sm"
                            onClick={() => handlePayNow(booking)}
                            disabled={payingBookingId === booking.id}
                            className="bg-primary hover:bg-primary/90"
                            data-testid={`pay-now-${booking.id}`}
                          >
                            {payingBookingId === booking.id ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <CreditCard className="w-4 h-4 mr-1" />
                            )}
                            <span>Pay Now</span>
                          </Button>
                        )}
                        {['pending', 'confirmed'].includes(booking.status) && (
                          <>
                            {booking.total_amount > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadInvoice(booking.id)}
                                className="border-primary/30 text-primary hover:bg-primary/10"
                                data-testid={`download-invoice-upcoming-${booking.id}`}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                <span className="hidden sm:inline">Invoice</span>
                              </Button>
                            )}
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => openRescheduleModal(booking)}
                              className="border-primary/30 text-primary hover:bg-primary/10"
                              data-testid={`reschedule-booking-${booking.id}`}
                            >
                              <CalendarClock className="w-4 h-4 mr-1" />
                              <span className="hidden sm:inline">Reschedule</span>
                            </Button>
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => openCancelModal(booking)}
                              className="border-destructive/30 text-destructive hover:bg-destructive/10"
                              data-testid={`cancel-booking-${booking.id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              <span className="hidden sm:inline">Cancel</span>
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Booking History */}
          <TabsContent value="history" className="space-y-4">
            {pastBookings.length === 0 ? (
              <Card className={`${cardBg} ${cardBorder}`}>
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-muted/10 flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-2">No booking history</h3>
                  <p className="text-muted-foreground">Your past bookings will appear here</p>
                </CardContent>
              </Card>
            ) : (
              pastBookings.map(booking => (
                <Card key={booking.id} className={`${cardBg} ${cardBorder}`}>
                  <CardContent className="p-5">
                    <div className="flex justify-between items-center">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-muted-foreground">#{booking.id?.slice(0, 8)}</span>
                          <Badge className={`${getStatusColor(booking.status)} border text-xs`}>{booking.status}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {booking.booking_date} • {booking.start_time} - {booking.end_time}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center">
                            <Building2 className="w-3.5 h-3.5 mr-1.5" />
                            {venueName(booking.venue_id)}
                          </div>
                          <div className="flex items-center">
                            <Users className="w-3.5 h-3.5 mr-1.5" />
                            {booking.attendees || 1} {(booking.attendees || 1) === 1 ? 'person' : 'people'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-heading font-semibold">₹{booking.total_amount?.toFixed(0)}</p>
                        {booking.status !== 'cancelled' && booking.total_amount > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadInvoice(booking.id)}
                            className="border-primary/30 text-primary hover:bg-primary/10"
                            data-testid={`download-invoice-${booking.id?.slice(0, 8)}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            <span className="hidden sm:inline">Invoice</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Invoices */}
          <TabsContent value="invoices" className="space-y-4">
            {invoices.length === 0 && pastBookings.filter(b => b.status !== 'cancelled' && b.total_amount > 0).length === 0 ? (
              <Card className={`${cardBg} ${cardBorder}`}>
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-muted/10 flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-2">No invoices yet</h3>
                  <p className="text-muted-foreground">Your invoices will appear here after payment</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {invoices.map(invoice => (
                  <Card key={invoice.id} className={`${cardBg} ${cardBorder} hover:border-primary/30 transition-all`}>
                    <CardContent className="p-5">
                      <div className="flex justify-between items-center">
                        <div className="space-y-1">
                          <p className="font-mono font-medium">{invoice.invoice_number}</p>
                          <p className="text-sm text-muted-foreground">{invoice.invoice_date}</p>
                        </div>
                        <div className="text-right flex items-center gap-4">
                          <div>
                            <p className="font-heading font-semibold">₹{invoice.total_amount?.toFixed(0)}</p>
                            <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'} className="text-xs">
                              {invoice.status}
                            </Badge>
                          </div>
                          {invoice.booking_id && (
                            <Button variant="outline" size="sm" onClick={() => handleDownloadInvoice(invoice.booking_id)}
                              className="border-primary/30 text-primary hover:bg-primary/10"
                              data-testid={`download-invoice-tab-${invoice.id}`}>
                              <Download className="w-4 h-4 mr-1" /> PDF
                            </Button>
                          )}
                          {invoice.zoho_invoice_url && (
                            <a href={invoice.zoho_invoice_url} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="sm" className="text-primary">
                                <ArrowUpRight className="w-4 h-4" />
                              </Button>
                            </a>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {/* Also show bookings without formal invoices */}
                {pastBookings.filter(b => b.status !== 'cancelled' && b.total_amount > 0 && !invoices.some(inv => inv.booking_id === b.id)).map(booking => (
                  <Card key={booking.id} className={`${cardBg} ${cardBorder} hover:border-primary/30 transition-all`}>
                    <CardContent className="p-5">
                      <div className="flex justify-between items-center">
                        <div className="space-y-1">
                          <p className="font-mono font-medium text-sm">Booking #{booking.id?.slice(0, 8)}</p>
                          <p className="text-sm text-muted-foreground">{booking.booking_date} • {booking.start_time} - {booking.end_time}</p>
                        </div>
                        <div className="text-right flex items-center gap-4">
                          <div>
                            <p className="font-heading font-semibold">₹{booking.total_amount?.toFixed(0)}</p>
                            <Badge variant={booking.status === 'completed' ? 'default' : 'secondary'} className="text-xs">
                              {booking.status}
                            </Badge>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => handleDownloadInvoice(booking.id)}
                            className="border-primary/30 text-primary hover:bg-primary/10"
                            data-testid={`download-booking-invoice-${booking.id?.slice(0, 8)}`}>
                            <Download className="w-4 h-4 mr-1" /> Invoice
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </>
            )}
          </TabsContent>

          {/* Credits */}
          <TabsContent value="credits" className="space-y-6">
            <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/20">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center text-primary">
                    <Coins className="w-5 h-5 mr-2" />
                    Credit Wallet
                    {wallet?.is_organization_wallet && (
                      <Badge variant="outline" className="ml-2 border-primary/30 text-primary">
                        Organization Wallet
                      </Badge>
                    )}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={fetchData} className="border-primary/30 text-primary">
                      <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                    <Button size="sm" onClick={() => setShowBuyCreditsModal(true)} className="bg-primary hover:bg-primary/90">
                      <Plus className="w-4 h-4 mr-1" /> Buy Credits
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Balance</p>
                    <p className="text-3xl font-heading font-bold text-primary mt-1" data-testid="credits-tab-total-balance">
                      {((wallet?.balance || 0) + (wallet?.topup_balance || 0)).toFixed(0)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {(wallet?.balance || 0).toFixed(0)} monthly + {(wallet?.topup_balance || 0).toFixed(0)} top-up
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Monthly Allocation</p>
                    <p className="text-3xl font-heading font-bold mt-1">
                      {wallet?.monthly_allocation?.toFixed(0) || '0'}
                    </p>
                    <p className="text-xs text-muted-foreground">{wallet?.auto_renew ? 'auto-renews on 1st' : 'no auto-renewal'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Used This Month</p>
                    <p className="text-3xl font-heading font-bold text-red-500 mt-1">
                      {wallet?.monthly_used?.toFixed(0) || '0'}
                    </p>
                    <p className="text-xs text-muted-foreground">credits spent</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Added This Month</p>
                    <p className="text-3xl font-heading font-bold text-emerald-500 mt-1">
                      {wallet?.monthly_added?.toFixed(0) || '0'}
                    </p>
                    <p className="text-xs text-muted-foreground">credits added</p>
                  </div>
                </div>
                {/* Each balance with its own expiry rule — purchased credits never
                    expire; sale bonus credits expire on their promo date. */}
                <div className={`mt-6 rounded-xl ${innerCardBg} border ${cardBorder} divide-y ${isDark ? 'divide-[#1e2128]' : 'divide-[#e5dfd0]'}`} data-testid="credits-expiry-breakdown">
                  <p className="px-4 pt-3 pb-2 text-sm text-muted-foreground">Balances &amp; expiry</p>
                  <div className="px-4 py-2.5 flex justify-between gap-3 text-sm">
                    <span>Purchased credits <span className="text-xs text-muted-foreground">(incl. any 2X bonus)</span></span>
                    <span className="font-semibold">{(wallet?.topup_balance || 0).toFixed(0)} · <span className="text-emerald-600">never expire</span></span>
                  </div>
                  {((wallet?.balance || 0) > 0 || (wallet?.monthly_allocation || 0) > 0) && (
                    <div className="px-4 py-2.5 flex justify-between gap-3 text-sm">
                      <span>Monthly plan credits</span>
                      <span className="font-semibold">
                        {(wallet?.balance || 0).toFixed(0)} · <span className="text-muted-foreground font-normal">
                          {wallet?.renewal_expiry_date
                            ? `valid till ${new Date(wallet.renewal_expiry_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' })}`
                            : wallet?.auto_renew ? 'renew monthly' : 'per your plan'}
                        </span>
                      </span>
                    </div>
                  )}
                  {(wallet?.promo_balance || 0) > 0 && (
                    <div className="px-4 py-2.5 flex justify-between gap-3 text-sm" data-testid="credits-promo-expiry">
                      <span>Bonus Meeting Room Credits <span className="text-xs text-muted-foreground">(sale offer · meeting rooms only, used first)</span></span>
                      <span className="font-semibold">
                        {Number(wallet.promo_balance).toLocaleString('en-IN', { maximumFractionDigits: 1 })} · <span className="text-amber-600">
                          expire {new Date(wallet.promo_valid_until).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
                <div className={`mt-6 p-4 rounded-xl ${innerCardBg} border ${cardBorder}`}>
                  <p className="text-sm text-muted-foreground mb-2">Credit Pricing</p>
                  <p className="font-medium">
                    <span className="text-primary">₹{wallet?.credit_price_base || 100}</span> per credit 
                    <span className="text-muted-foreground"> + </span>
                    <span className="text-primary">{wallet?.credit_gst_rate || 18}% GST</span>
                    <span className="text-muted-foreground"> = </span>
                    <span className="text-lg font-bold text-primary">₹{wallet?.credit_price || 118}</span>
                    <span className="text-muted-foreground"> total per credit</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className={`${cardBg} ${cardBorder}`}>
              <CardHeader>
                <CardTitle>Transaction History</CardTitle>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No transactions yet</p>
                ) : (
                  <div className="space-y-3">
                    {transactions.map(trans => (
                      <div key={trans.id} className={`flex justify-between items-center p-4 ${innerCardBg} rounded-xl border ${cardBorder}`}>
                        <div>
                          <p className="font-medium">{trans.description}</p>
                          <p className="text-xs text-muted-foreground capitalize">{trans.transaction_type?.replace('_', ' ')}</p>
                          {trans.payment_amount && (
                            <p className="text-xs text-muted-foreground">Paid: ₹{trans.payment_amount?.toFixed(0)}</p>
                          )}
                        </div>
                        <p className={`font-heading font-semibold ${trans.amount > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {trans.amount > 0 ? '+' : ''}{Math.abs(trans.amount)?.toFixed(0)} credits
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Day Passes */}
          <TabsContent value="day_passes" className="space-y-6">
            <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/20">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center text-primary">
                    <Ticket className="w-5 h-5 mr-2" />
                    Bulk Day Pass Balance
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {(dayPassBalance?.total_remaining || 0) > 0 && (
                      <Button size="sm" onClick={() => setShowQuickBookDayPass(true)} data-testid="quick-book-day-pass-open-btn-tab">
                        <Ticket className="w-4 h-4 mr-1" /> Book Now
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={fetchData} className="border-primary/30 text-primary">
                      <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground">Remaining</p>
                    <p className="text-3xl font-heading font-bold text-primary mt-1" data-testid="day-pass-total-remaining">
                      {dayPassBalance?.total_remaining || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Purchased</p>
                    <p className="text-3xl font-heading font-bold mt-1">
                      {(dayPassBalance?.packages || []).reduce((sum, p) => sum + (p.quantity_purchased || 0), 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Used</p>
                    <p className="text-3xl font-heading font-bold text-red-500 mt-1">
                      {(dayPassBalance?.packages || []).reduce((sum, p) => sum + (p.quantity_used || 0), 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`${cardBg} ${cardBorder}`}>
              <CardHeader>
                <CardTitle>Your Packages</CardTitle>
              </CardHeader>
              <CardContent>
                {(dayPassBalance?.packages || []).length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No Bulk Day Pass packages yet. Contact Sales to purchase a bulk package.</p>
                ) : (
                  <div className="space-y-3">
                    {dayPassBalance.packages.map(pkg => {
                      const expiryDate = new Date(pkg.expiry_date);
                      const startDate = pkg.start_date ? new Date(pkg.start_date) : null;
                      const isScheduled = pkg.status === 'active' && startDate && startDate > new Date();
                      const daysToExpiry = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
                      const expiringSoon = pkg.status === 'active' && !isScheduled && daysToExpiry >= 0 && daysToExpiry <= 30;
                      return (
                        <div key={pkg.id} className={`flex justify-between items-center p-4 ${innerCardBg} rounded-xl border ${cardBorder}`}>
                          <div>
                            <div className="flex items-center gap-2">
                              {isScheduled ? (
                                <Badge variant="outline" className="border-amber-500/40 text-amber-600">Scheduled</Badge>
                              ) : (
                                <Badge variant="outline" className="capitalize">{pkg.status}</Badge>
                              )}
                              {pkg.offer && (
                                <Badge variant="outline" className="border-amber-500/40 text-amber-600" data-testid="promo-package-badge">
                                  {pkg.sold_by_name || 'Sale offer'} · Free
                                </Badge>
                              )}
                              {expiringSoon && (
                                <Badge variant="outline" className="border-amber-500/40 text-amber-600">
                                  Expires in {daysToExpiry}d
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {isScheduled && <>Starts {startDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · </>}
                              Expires {expiryDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                          <p className="font-heading font-semibold text-primary">
                            {pkg.quantity_remaining} / {pkg.quantity_purchased} left
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="services">
            <ServicesTab api={api} venues={venues} theme={{ cardBg, cardBorder, innerCardBg: isDark ? 'bg-[#13161a]' : 'bg-[#faf8f4]' }} />
          </TabsContent>

          {user?.organization_id && (
            <TabsContent value="organization">
              <OrganizationTab />
            </TabsContent>
          )}

          <TabsContent value="tickets" className="space-y-6">
              {/* Raise New Ticket */}
              <Card className={`${cardBg} ${cardBorder}`}>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Report an Issue</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Venue (optional)</Label>
                    <Select value={ticketVenueId} onValueChange={setTicketVenueId}>
                      <SelectTrigger data-testid="ticket-venue-select">
                        <SelectValue placeholder="Select venue..." />
                      </SelectTrigger>
                      <SelectContent>
                        {venues.map(v => (
                          <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Description <span className="text-red-500">*</span></Label>
                    <textarea
                      value={ticketDesc}
                      onChange={(e) => setTicketDesc(e.target.value)}
                      placeholder="Describe the issue or snag in detail..."
                      className="w-full min-h-[120px] rounded-lg border border-border bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
                      data-testid="ticket-description"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Attach Photo (optional)</Label>
                    <div className="flex items-center gap-3">
                      {ticketPhoto ? (
                        <div className="relative">
                          <img
                            src={ticketPhoto.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${ticketPhoto}` : ticketPhoto}
                            alt="Ticket"
                            className="w-20 h-20 rounded-lg object-cover border border-border"
                          />
                          <button
                            onClick={() => setTicketPhoto(null)}
                            className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center text-xs"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed ${cardBorder} cursor-pointer hover:border-primary/40 transition-colors`}>
                          {ticketUploading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <ImagePlus className="w-4 h-4 text-muted-foreground" />
                          )}
                          <span className="text-sm text-muted-foreground">Upload Photo</span>
                          <input type="file" accept="image/*" className="hidden" onChange={handleTicketPhotoUpload} disabled={ticketUploading} />
                        </label>
                      )}
                    </div>
                  </div>

                  <Button
                    onClick={handleSubmitTicket}
                    disabled={submittingTicket || !ticketDesc.trim()}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="submit-ticket-btn"
                  >
                    {submittingTicket ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Submit Ticket
                  </Button>
                </CardContent>
              </Card>

              {/* Ticket History */}
              {tickets.length > 0 && (
                <Card className={`${cardBg} ${cardBorder}`}>
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Your Tickets</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {tickets.map(ticket => (
                      <div key={ticket.id} className={`p-4 rounded-lg border ${cardBorder} space-y-2`} data-testid={`ticket-${ticket.id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm">{ticket.description}</p>
                            {ticket.venue_name && (
                              <p className="text-xs text-muted-foreground mt-1">Venue: {ticket.venue_name}</p>
                            )}
                          </div>
                          <Badge className={`${getTicketStatusColor(ticket.status)} border shrink-0`}>
                            {ticket.status?.replace('_', ' ')}
                          </Badge>
                        </div>
                        {ticket.photo_url && (
                          <img
                            src={ticket.photo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${ticket.photo_url}` : ticket.photo_url}
                            alt="Issue"
                            className="w-32 h-32 rounded-lg object-cover border border-border"
                          />
                        )}
                        {ticket.admin_notes && (
                          <div className={`p-2 rounded-md ${innerCardBg} text-xs`}>
                            <span className="font-medium">Response:</span> {ticket.admin_notes}
                          </div>
                        )}
                        <p className="text-[10px] text-muted-foreground">
                          {new Date(ticket.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Virtual Office subscriptions */}
            <TabsContent value="virtual_office" className="space-y-4">
              <ClientVirtualOfficeView api={api} cardBg={cardBg} cardBorder={cardBorder} navigate={navigate} />
            </TabsContent>

            {/* Clayworks Ecosystem */}
            <TabsContent value="ecosystem" className="space-y-4">
              <ClientEcosystemPanel api={api} navigate={navigate} cardBg={cardBg} cardBorder={cardBorder} />
            </TabsContent>
        </Tabs>

        <ProfileModal
          open={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          user={user}
          api={api}
          onProfileUpdate={() => window.location.reload()}
        />

        <QuickBookDayPassModal
          open={showQuickBookDayPass}
          onClose={() => setShowQuickBookDayPass(false)}
          api={api}
          user={user}
          venues={venues}
          dayPassBalance={dayPassBalance}
          onBooked={fetchData}
        />
      </div>

      {/* Buy Credits Modal - Tier Selection */}
      <Dialog open={showBuyCreditsModal && !showCheckout} onOpenChange={(open) => { setShowBuyCreditsModal(open); if (!open) { setSelectedTier(null); setShowCheckout(false); }}}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Buy Credits</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className={`p-4 rounded-xl ${innerCardBg} border ${cardBorder} mb-6`}>
              <p className="text-sm text-muted-foreground">Current Balance</p>
              <p className="text-2xl font-heading font-bold text-primary">
                {((wallet?.balance || 0) + (wallet?.topup_balance || 0)).toFixed(0)} credits
              </p>
              {(wallet?.topup_balance || 0) > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {(wallet?.balance || 0).toFixed(0)} monthly · {(wallet?.topup_balance || 0).toFixed(0)} top-up (never expires)
                </p>
              )}
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">Select a credit plan</p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {creditTiers.map((tier) => {
                // Sale 2X offer: same price, double the credits (replaces the
                // "% OFF" framing while the offer is live).
                const bonus = creditPurchaseBonus(tier.credits, user);
                return (
                <div
                  key={tier.credits}
                  onClick={() => handleSelectTier(tier)}
                  className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all hover:border-primary/50 ${
                    tier.discount_percent > 0 ? 'border-primary/30' : `border ${cardBorder}`
                  } ${isDark ? 'bg-[#0a0b0d]' : 'bg-gray-50'}`}
                  data-testid={`tier-${tier.credits}`}
                >
                  {bonus > 0 ? (
                    <Badge className="absolute -top-2 -right-2 bg-green-500 text-white text-xs" data-testid={`tier-2x-badge-${tier.credits}`}>
                      2X CREDITS
                    </Badge>
                  ) : tier.discount_percent > 0 && (
                    <Badge className="absolute -top-2 -right-2 bg-green-500 text-white text-xs">
                      {tier.discount_percent}% OFF
                    </Badge>
                  )}
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-1">{tier.label}</p>
                    <p className="text-2xl font-heading font-bold text-primary">{tier.credits}</p>
                    {bonus > 0 ? (
                      <>
                        <p className="text-sm font-bold text-green-600" data-testid={`tier-bonus-${tier.credits}`}>+ {bonus} FREE</p>
                        <p className="text-xs text-muted-foreground mb-2">= {tier.credits + bonus} credits</p>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground mb-2">credits</p>
                    )}
                    <div className="border-t border-border pt-2 mt-2">
                      {tier.discount_percent > 0 && !bonus && (
                        <p className="text-xs text-muted-foreground line-through">
                          ₹{tier.base_amount.toLocaleString()}
                        </p>
                      )}
                      <p className="text-lg font-semibold">
                        ₹{tier.discounted_amount.toLocaleString()}
                      </p>
                      <p className="text-xs text-muted-foreground">+ GST</p>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            {creditPurchaseBonus(100, user) > 0 ? (
              <div className="mt-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-center" data-testid="credits-2x-note">
                <p className="text-sm font-semibold text-green-700 dark:text-green-400">
                  {promotion?.name || 'Sale offer'}: buy any plan and get the same number of credits free.
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Offer ends {promotion?.ends_at ? new Date(promotion.ends_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' }) : 'soon'} · purchased and bonus credits never expire · GST applied at checkout
                </p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center mt-4">
                Base price: ₹100 per credit • GST applied at checkout
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkout Modal */}
      <Dialog open={showCheckout} onOpenChange={(open) => { setShowCheckout(open); if (!open) setSelectedTier(null); }}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Checkout</DialogTitle>
          </DialogHeader>
          {selectedTier && (
            <div className="grid gap-4 py-4">
              <div className={`p-4 rounded-xl ${innerCardBg} border ${cardBorder}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">{selectedTier.label} Plan</span>
                  <Badge variant="outline">
                    {creditPurchaseBonus(selectedTier.credits, user) > 0
                      ? `${selectedTier.credits} + ${creditPurchaseBonus(selectedTier.credits, user)} free credits`
                      : `${selectedTier.credits} credits`}
                  </Badge>
                </div>
              </div>
              
              <div className={`p-4 rounded-xl ${innerCardBg} border ${cardBorder}`}>
                <p className="text-sm font-medium mb-3">Payment Breakdown</p>
                <div className="space-y-2">
                  {creditPurchaseBonus(selectedTier.credits, user) > 0 ? (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{selectedTier.credits} credits</span>
                        <span>₹{selectedTier.discounted_amount.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm text-green-600 font-medium" data-testid="checkout-2x-bonus">
                        <span>+ {creditPurchaseBonus(selectedTier.credits, user)} bonus credits ({promotion?.name || 'sale offer'})</span>
                        <span>FREE</span>
                      </div>
                      <div className="flex justify-between text-sm font-medium">
                        <span>You receive</span>
                        <span>{selectedTier.credits + creditPurchaseBonus(selectedTier.credits, user)} credits · never expire</span>
                      </div>
                    </>
                  ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Base Amount ({selectedTier.credits} × ₹100)</span>
                    <span>₹{selectedTier.base_amount.toLocaleString()}</span>
                  </div>
                  )}
                  {selectedTier.discount_percent > 0 && !creditPurchaseBonus(selectedTier.credits, user) && (
                    <div className="flex justify-between text-sm text-green-500">
                      <span>Discount ({selectedTier.discount_percent}%)</span>
                      <span>- ₹{selectedTier.discount_amount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>₹{selectedTier.discounted_amount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">GST (18%)</span>
                    <span>₹{selectedTier.gst_amount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2 border-border">
                    <span>Total Payable</span>
                    <span className="text-primary">₹{selectedTier.total_amount.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground text-center">
                Payment will be processed securely via Razorpay
              </p>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setShowCheckout(false); }} 
              className={cardBorder}
            >
              Back
            </Button>
            <Button 
              onClick={handleBuyCredits} 
              disabled={buyingCredits} 
              className="bg-primary hover:bg-primary/90"
              data-testid="pay-btn"
            >
              {buyingCredits ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
              Pay ₹{selectedTier?.total_amount?.toLocaleString()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Modal */}
      <Dialog open={showCancelModal} onOpenChange={(open) => { setShowCancelModal(open); if (!open) { setSelectedBooking(null); setRefundEstimate(null); }}}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Cancel Booking
            </DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="grid gap-4 py-4">
              <div className={`p-4 rounded-xl ${innerCardBg} border ${cardBorder}`}>
                <p className="text-sm text-muted-foreground">Booking Details</p>
                <p className="font-medium mt-1">{selectedBooking.booking_date} • {selectedBooking.start_time} - {selectedBooking.end_time}</p>
                <p className="text-sm text-muted-foreground mt-1">Booking #{selectedBooking.id?.slice(0, 8)}</p>
              </div>
              
              {loadingRefund ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : refundEstimate && (
                <div className={`p-4 rounded-xl ${innerCardBg} border ${cardBorder}`}>
                  <p className="text-sm font-medium mb-3">Refund Information</p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Policy</span>
                      <Badge variant="outline" className="text-xs">{refundEstimate.policy_tier}</Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Refund Percentage</span>
                      <span className={refundEstimate.refund_percentage === 0 ? 'text-destructive' : 'text-green-500'}>
                        {refundEstimate.refund_percentage}%
                      </span>
                    </div>
                    {refundEstimate.credits_used > 0 && (
                      <div className="flex justify-between text-sm font-medium border-t border-border pt-2 mt-2">
                        <span>Credits to Refund</span>
                        <span className="text-primary">{refundEstimate.credits_refund?.toFixed(0)} credits</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className={`p-4 rounded-xl bg-destructive/10 border border-destructive/30`}>
                <p className="text-sm font-medium text-destructive mb-2">Cancellation Policy</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• 48+ hours: 100% refund</li>
                  <li>• 24-48 hours: 75% refund</li>
                  <li>• 12-24 hours: 50% refund</li>
                  <li>• 6-12 hours: 25% refund</li>
                  <li>• Less than 3 hours: No refund</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setShowCancelModal(false); setSelectedBooking(null); setRefundEstimate(null); }} 
              className={cardBorder}
            >
              Keep Booking
            </Button>
            <Button 
              onClick={() => handleCancelBooking(selectedBooking?.id)} 
              disabled={cancelling}
              variant="destructive"
            >
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
              Confirm Cancellation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Booking Modal */}
      <Dialog open={showRescheduleModal} onOpenChange={(open) => { setShowRescheduleModal(open); if (!open) setSelectedBooking(null); }}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-[#13161a] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-primary" />
              Reschedule Booking
            </DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="grid gap-4 py-4">
              <div className={`p-4 rounded-xl ${innerCardBg} border ${cardBorder}`}>
                <p className="text-sm text-muted-foreground">Current Booking</p>
                <p className="font-medium mt-1">{selectedBooking.booking_date} • {selectedBooking.start_time} - {selectedBooking.end_time}</p>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>New Date *</Label>
                  <Input 
                    type="date"
                    value={rescheduleForm.new_date}
                    onChange={(e) => setRescheduleForm({...rescheduleForm, new_date: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    className={isDark ? 'bg-[#0a0b0d] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>New Start Time *</Label>
                    <Select value={rescheduleForm.new_start_time} onValueChange={(v) => setRescheduleForm({...rescheduleForm, new_start_time: v, new_end_time: ''})}>
                      <SelectTrigger className={isDark ? 'bg-[#0a0b0d] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}>
                        <SelectValue placeholder="Start" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>New End Time *</Label>
                    <Select value={rescheduleForm.new_end_time} onValueChange={(v) => setRescheduleForm({...rescheduleForm, new_end_time: v})}>
                      <SelectTrigger className={isDark ? 'bg-[#0a0b0d] border-[#1e2128]' : 'bg-white border-[#e5dfd0]'}>
                        <SelectValue placeholder="End" />
                      </SelectTrigger>
                      <SelectContent>
                        {getEndSlots(rescheduleForm.new_start_time).map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-xl bg-blue-500/10 border border-blue-500/30`}>
                <p className="text-sm font-medium text-blue-400 mb-2">Rescheduling Policy</p>
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>• More than 3 hours before booking: <span className="font-semibold text-green-500">No fee</span></p>
                  <p>• 1-3 hours before booking: <span className="font-semibold text-amber-500">15% fee</span></p>
                  <p>• Less than 1 hour before: <span className="font-semibold text-red-500">Not allowed</span></p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => { setShowRescheduleModal(false); setSelectedBooking(null); }} 
              className={cardBorder}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleReschedule} 
              disabled={rescheduling}
              className="bg-primary hover:bg-primary/90"
            >
              {rescheduling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CalendarClock className="w-4 h-4 mr-2" />}
              Reschedule Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDashboard;
